import { createApiClient, type TokenStore } from '@nexo/api';
import type { AuthTokens } from '@nexo/types';
import { onLanguage } from './i18n';
import { isDesktop } from './platform';

const TOKENS_KEY = 'nexo.tokens';
export const SESSION_EXPIRED_EVENT = 'nexo:session-expired';

/**
 * Tokens live in localStorage. Both the web app and the Tauri webview use it; the desktop app
 * can later move them to the OS keychain behind this same interface (see ADR 0003).
 */
const tokenStore: TokenStore = {
  get() {
    try {
      const raw = localStorage.getItem(TOKENS_KEY);
      return raw ? (JSON.parse(raw) as AuthTokens) : null;
    } catch {
      return null;
    }
  },
  set(tokens) {
    localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
  },
  clear() {
    localStorage.removeItem(TOKENS_KEY);
  },
};

export const apiBaseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

// Read on every request: the API answers errors (and sends emails) in the interface language.
const headers: Record<string, string> = {
  // The desktop webview's user agent looks like a browser's: say which app this is.
  'X-Nexo-Client': isDesktop ? 'desktop' : 'web',
};
onLanguage((language) => {
  headers['Accept-Language'] = language;
});

export const api = createApiClient({
  baseUrl: apiBaseUrl,
  tokens: tokenStore,
  onSessionExpired: () => window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT)),
  headers,
});
